import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import {
  GetSharedCandidatesRosterDataOutput,
  useSendShortlistRequestInterviewEmailMutation,
} from '$store/modules/candidates';
import { ReactComponent as GoTo } from '$icons/ForwardArrow.svg';
import { ReactComponent as CloseModal } from '$icons/uncolored/Close.svg';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import capitalizedName from '$utils/capitalizedName';
import { SIMERA_FEE } from '$constants/SimeraOperations';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import SPKCandidateCard from './SPKCandidateCard/SPKCandidateCard';
import SPKPanel from './SPKPanel/SPKPanel';
import SPKVideoModal from './SPKVideoModal/SPKVideoModal';
import SPKRequestInterviewModal from './SPKRequestInterviewModal/SPKRequestInterviewModal';

const SPKCandidateList = ({
  candidates,
  shareCandidatesIds,
  shareReferenceId,
  feeAmount,
  shortlistTitle,
}: {
  candidates: GetSharedCandidatesRosterDataOutput['candidates'];
  shareCandidatesIds?: string;
  shareReferenceId?: string;
  feeAmount?: number;
  shortlistTitle?: string;
}) => {
  const resolvedFee = feeAmount ?? SIMERA_FEE;
  const [showCandidateSidebar, setShowCandidateSidebar] = useState(false);
  const [showLoginOrRegisterModal, setShowLoginOrRegisterModal] =
    useState(false);
  const [showRequestInterviewModal, setShowRequestInterviewModal] =
    useState(false);
  const [selectedCandidate, setSetSelectedCandidate] = useState<
    undefined | GetSharedCandidatesRosterDataOutput['candidates'][number]
  >();
  const [isOpenSmartScore, setIsOpenSmartScore] = useState(false);
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);

  const { isLoggedIn } = useAppSelector((state) => state.auth);
  const [sendShortlistRequestInterviewEmail, { isLoading: isSendingRequest }] =
    useSendShortlistRequestInterviewEmailMutation();

  const navigate = useNavigate();
  const [t] = useTranslation('global');

  const modalContent = useRef<HTMLDivElement>(null);

  const handleCloseShowCandidate = () => {
    modalContent.current?.classList.remove('translate-x-0');
    modalContent.current?.classList.add('translate-x-full');
    setTimeout(() => {
      setSetSelectedCandidate(undefined);
      setShowCandidateSidebar(false);
    }, 500);
  };

  const showCandidateSidebarHandler = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number],
    smartScoreIsOpen?: boolean
  ) => {
    setShowCandidateSidebar(true);
    setSetSelectedCandidate(candidateData);
    setIsOpenSmartScore(smartScoreIsOpen || false);
  };

  useEffect(() => {
    if (showCandidateSidebar) {
      modalContent.current?.classList.remove('translate-x-full');
      modalContent.current?.classList.add('translate-x-0');
    }
  }, [showCandidateSidebar]);

  const requestInterviewHandler = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number]
  ) => {
    setSetSelectedCandidate(candidateData);
    if (isLoggedIn && shortlistTitle) {
      setShowRequestInterviewModal(true);
      return;
    }
    setShowLoginOrRegisterModal(true);
  };

  const confirmRequestInterviewHandler = async () => {
    if (!selectedCandidate || !shortlistTitle) return;

    await sendShortlistRequestInterviewEmail({
      CandidateId: selectedCandidate.Id,
      ShortlistTitle: shortlistTitle,
    });
    setShowRequestInterviewModal(false);
  };

  return (
    <>
      {/* Panel Modal */}
      <ModalInTailwind
        isOpen={showCandidateSidebar}
        closeModal={handleCloseShowCandidate}
        position="right"
      >
        <SPKPanel
          candidate={
            selectedCandidate as GetSharedCandidatesRosterDataOutput['candidates'][number]
          }
          parentRef={modalContent}
          onClose={handleCloseShowCandidate}
          smartScoreInitialStatus={isOpenSmartScore}
          feeAmount={resolvedFee}
        />
      </ModalInTailwind>
      {/* Video Modal */}
      <ModalInTailwind
        isOpen={isOpenVideoModal}
        closeModal={() => setIsOpenVideoModal(false)}
      >
        <SPKVideoModal
          selectedCandidate={
            selectedCandidate as GetSharedCandidatesRosterDataOutput['candidates'][number]
          }
        />
      </ModalInTailwind>
      {/* Request interview confirmation modal */}
      <ModalInTailwind
        isOpen={showRequestInterviewModal}
        closeModal={() => setShowRequestInterviewModal(false)}
      >
        <SPKRequestInterviewModal
          candidateName={`${capitalizedName(
            selectedCandidate?.User?.FirstName
          )} ${capitalizedName(selectedCandidate?.User?.LastName)}.`}
          isLoading={isSendingRequest}
          onCancel={() => setShowRequestInterviewModal(false)}
          onConfirm={confirmRequestInterviewHandler}
        />
      </ModalInTailwind>
      {/* Login or register modal */}
      <ModalInTailwind
        isOpen={showLoginOrRegisterModal}
        closeModal={() => setShowLoginOrRegisterModal(false)}
      >
        <div className="max-w-2xl w-full bg-white p-4 md:p-8 mx-2 rounded-2xl relative">
          <div>
            <span className="font-bold">
              {t('sharedPosition.sharedCandidatesRoster.modalTitle')}
            </span>
            <button
              aria-label="close modal"
              type="button"
              onClick={() => {
                setShowLoginOrRegisterModal(false);
              }}
            >
              <CloseModal className="absolute right-0 top-0 mr-2 mt-2 stroke-employer-primary" />
            </button>
          </div>
          <hr className="my-4" />
          <div className="flex align-middle items-center gap-6 font-semibold flex-col sm:flex-row">
            <div className="flex align-middle items-center flex-1 w-full">
              <div className="w-12 mr-2">
                <DefaultIcon2
                  picture={selectedCandidate?.User?.ProfilePicture?.Url}
                  firstName={selectedCandidate?.User.FirstName}
                  lastName={selectedCandidate?.User?.LastName}
                />
              </div>
              <span className="flex-1">
                {capitalizedName(selectedCandidate?.User?.FirstName)}{' '}
                {capitalizedName(selectedCandidate?.User?.LastName)}.
              </span>
            </div>
            <div className="flex flex-row gap-6">
              <button
                type="button"
                className="text-employer-primary"
                onClick={() => {
                  navigate('/client_login', {
                    state: { shareCandidatesIds, shareReferenceId },
                  });
                }}
              >
                {t('sharedPosition.login')}
              </button>
              <button
                type="button"
                className="flex align-middle items-center gap-2 text-white bg-employer-primary rounded p-3"
                onClick={() => {
                  navigate('/client_register', {
                    state: { shareCandidatesIds, shareReferenceId },
                  });
                }}
              >
                {t('sharedPosition.createAccount')}
                <GoTo className="fill-white" />
              </button>
            </div>
          </div>
        </div>
      </ModalInTailwind>
      <div className="gap-4 flex flex-col">
        {candidates.map((singleCandidate) => {
          return (
            <SPKCandidateCard
              key={`skills_candidate_${singleCandidate.Id}`}
              candidate={singleCandidate}
              showSidebarHandler={showCandidateSidebarHandler}
              requestInterviewHandler={() =>
                requestInterviewHandler(singleCandidate)
              }
              feeAmount={resolvedFee}
            />
          );
        })}
      </div>
    </>
  );
};

SPKCandidateList.defaultProps = {
  shareCandidatesIds: '',
  shareReferenceId: '',
  hideBottomButtons: false,
  feeAmount: undefined,
  shortlistTitle: undefined,
};

export default SPKCandidateList;
